import axios from 'axios';

const AZURE_MAPS_API_KEY = process.env.REACT_APP_AZURE_MAPS;
const AZURE_MAPS_SEARCH_API = 'https://atlas.microsoft.com/search/address/json';
const AZURE_MAPS_REVERSE_SEARCH_API = 'https://atlas.microsoft.com/search/address/reverse/json';

export interface GeocodingResult {
    latitude: number;
    longitude: number;
    address?: string;
}

// Simple function to geocode an address to coordinates
export async function geocodeAddress(address: string): Promise<GeocodingResult | null> {
    try {
        // Check if input looks like coordinates (latitude, longitude)
        const coordsRegex = /^\s*([-+]?\d+\.?\d*)\s*[,\s]\s*([-+]?\d+\.?\d*)\s*$/;
        const coordsMatch = address.match(coordsRegex);
        
        if (coordsMatch) {
            // Extract latitude and longitude from the input
            const latitude = parseFloat(coordsMatch[1]);
            const longitude = parseFloat(coordsMatch[2]);
            
            // Validate coordinates
            if (!isNaN(latitude) && !isNaN(longitude) && 
                latitude >= -90 && latitude <= 90 && 
                longitude >= -180 && longitude <= 180) {
                
                // Use reverse geocoding to get the address for these coordinates
                const result = await reverseGeocodeCoordinates(latitude, longitude);
                return {
                    latitude,
                    longitude,
                    address: result?.address || address
                };
            }
        }
        
        // If not coordinates or invalid coordinates, use standard geocoding
        const response = await axios.get(AZURE_MAPS_SEARCH_API, {
            params: {
                'subscription-key': AZURE_MAPS_API_KEY,
                'api-version': '1.0',
                'query': address,
                'limit': 1,
                'typeahead': false
            }
        });

        const results = response.data.results;
        if (results && results.length > 0) {
            const position = results[0].position;
            return {
                latitude: position.lat,
                longitude: position.lon,
                address: results[0].address?.freeformAddress || address
            };
        }
        return null;
    } catch (error) {
        console.error('Error geocoding address:', error);
        return null;
    }
}

// Helper function to format coordinates as a string
function formatCoordinatesAsString(latitude: number, longitude: number): string {
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

export async function reverseGeocodeCoordinates(latitude: number, longitude: number): Promise<GeocodingResult | null> {
    try {
        const response = await axios.get(AZURE_MAPS_REVERSE_SEARCH_API, {
            params: {
                'subscription-key': AZURE_MAPS_API_KEY,
                'api-version': '1.0',
                'query': `${latitude},${longitude}`,
                // Add more parameters for better results
                'returnSpeedLimit': false,
                'returnRoadUse': false,
                'returnMatchType': false,
                'view': 'Unified',  // Get the most complete address information
                'language': 'en-US'
            }
        });

        if (response.data.addresses && response.data.addresses.length > 0) {
            const address = response.data.addresses[0].address;
            
            // Check if we have meaningful address components
            const hasStreetInfo = address.streetName || address.streetNumber;
            const hasLocalityInfo = address.municipality || address.postalCode;
            
            // Only process address if it has meaningful components
            if (hasStreetInfo || hasLocalityInfo) {
                // Build a more complete address string
                let formattedAddress = '';
                
                // Try to build a more complete address from components
                if (address.streetName || address.streetNumber) {
                    formattedAddress += `${address.streetNumber || ''} ${address.streetName || ''}`;
                }
                
                if (address.municipality) {
                    if (formattedAddress) formattedAddress += ', ';
                    formattedAddress += address.municipality;
                }
                
                if (address.countrySubdivision) {
                    if (formattedAddress) formattedAddress += ', ';
                    formattedAddress += address.countrySubdivision;
                }
                
                if (address.postalCode) {
                    if (formattedAddress) formattedAddress += ' ';
                    formattedAddress += address.postalCode;
                }
                
                // If we have a meaningful address, return it
                if (formattedAddress) {
                    return {
                        latitude,
                        longitude,
                        address: formattedAddress.trim()
                    };
                }
                
                // If we couldn't build a good address from components, check freeformAddress
                if (address.freeformAddress) {
                    // Only use freeformAddress if it doesn't look like just a country or region
                    // (which would indicate a remote location with no specific address)
                    if (address.freeformAddress.split(',').length > 1) {
                        return {
                            latitude,
                            longitude,
                            address: address.freeformAddress
                        };
                    }
                }
            }
        }
        
        // Use formatted coordinates as the address when no address is found
        return {
            latitude,
            longitude,
            address: formatCoordinatesAsString(latitude, longitude)
        };
    } catch (error) {
        console.error('Error reverse geocoding coordinates:', error);
        // Use formatted coordinates as the address when no address is found
        return {
            latitude,
            longitude,
            address: formatCoordinatesAsString(latitude, longitude)
        };
    }
}
